/* constants.ts — enumerations + small pure helpers. */

import type { Severity, Status, Tag } from './types';

// The API answers 403 to any cookie-authed request that omits this header.
export const CSRF_HEADERS = { 'X-Zoe-Csrf': '1' } as const;
export const JSON_HEADERS = { 'Content-Type': 'application/json', ...CSRF_HEADERS } as const;

export const SEVERITIES: { id: Severity; label: string; dot: string }[] = [
  { id: 'low', label: 'Low', dot: '#7A7E88' },
  { id: 'medium', label: 'Medium', dot: '#087A70' },
  { id: 'high', label: 'High', dot: '#C08A1E' },
  { id: 'blocker', label: 'Blocker', dot: '#C94A3E' },
];

export type Tone = 'neutral' | 'teal' | 'amber' | 'ok' | 'muted';

export const STATUSES: { id: Status; label: string; tone: Tone }[] = [
  { id: 'open', label: 'Open', tone: 'neutral' },
  { id: 'triaged', label: 'Triaged', tone: 'teal' },
  { id: 'in-progress', label: 'In progress', tone: 'amber' },
  { id: 'done', label: 'Done', tone: 'ok' },
  { id: 'wontfix', label: "Won't fix", tone: 'muted' },
];

export const TAGS: Tag[] = ['comment', 'bug', 'idea', 'praise', 'question'];

export const SEVERITY_RANK: Record<Severity, number> = {
  low: 0,
  medium: 1,
  high: 2,
  blocker: 3,
};

export const TONE_STYLE: Record<Tone, { bg: string; fg: string }> = {
  neutral: { bg: 'rgba(0,0,0,0.04)', fg: 'var(--p-ink)' },
  teal: { bg: 'rgba(43,191,179,0.12)', fg: 'var(--p-teal)' },
  amber: { bg: 'rgba(192,138,30,0.12)', fg: 'var(--p-amber)' },
  ok: { bg: 'rgba(43,191,179,0.18)', fg: 'var(--p-teal)' },
  muted: { bg: 'rgba(0,0,0,0.03)', fg: 'var(--p-ink-3)' },
};

export function statusMeta(id: string): { id: string; label: string; tone: Tone } {
  return STATUSES.find((s) => s.id === id) ?? { id, label: id, tone: 'neutral' };
}

export function sevMeta(id: string): { id: string; label: string; dot: string } {
  return SEVERITIES.find((s) => s.id === id) ?? { id, label: id, dot: '#7A7E88' };
}

export function newId(): string {
  return 't-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 5);
}

export function newCommentId(): string {
  return 'c-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 4);
}

/** Relative time: "just now" / "5m ago" / "3h ago" / "2d ago" / locale date. */
export function relTime(iso: string | undefined): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const s = Math.floor(diff / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

export function viewport(): string {
  return `${window.innerWidth}×${window.innerHeight}`;
}
